/*============================================================================
  Custom Cart Drawer
  - Integrates with existing Expanse theme cart functionality
==============================================================================*/
theme.CartDrawer = (function() {
  var selectors = {
    drawer: '.cart__drawer',
    drawerTrigger: '#HeaderCartTrigger',
    closeBtn: '.js-close-cart-drawer',
    cartForm: '.cart__drawer-form',
    products: '[data-products]',
    discounts: '[data-discounts]',
    subtotal: '[data-subtotal]',
    checkoutBtn: '.cart__checkout',
    noteBtn: '.add-note',
    cartNote: '.cart__note',
    termsCheckbox: '.cart__terms-checkbox',
  };

  var classes = {
    drawerOpen: 'show-cart__drawer',
    bodyOpen: 'cv--show-cart__drawer',
    btnLoading: 'btn--loading',
    hide: 'hide',
  };

  var config = {
    namespace: '.cart-drawer',
    isOpen: false,
  };

  function CartDrawer() {
    this.drawer = document.querySelector(selectors.drawer);
    
    if (!this.drawer) {
      return;
    }

    this.form = this.drawer.querySelector(selectors.cartForm);
    this.products = this.drawer.querySelector(selectors.products);
    this.discounts = this.drawer.querySelector(selectors.discounts);
    this.subtotal = this.drawer.querySelector(selectors.subtotal);
    this.noteBtn = this.drawer.querySelector(selectors.noteBtn);
    this.cartNote = this.drawer.querySelector(selectors.cartNote);
    this.checkoutBtn = this.drawer.querySelector(selectors.checkoutBtn);
    this.termsCheckbox = this.drawer.querySelector(selectors.termsCheckbox);

    this.init();
  }

  CartDrawer.prototype = Object.assign({}, CartDrawer.prototype, {
    init: function() {
      // Initialize cart form functionality
      if (this.form) {
        this.cartForm = new theme.CartForm(this.form);
        this.cartForm.buildCart();
      }

      // Setup open triggers
      document.querySelectorAll(selectors.drawerTrigger).forEach(trigger => {
        trigger.addEventListener('click', this.open.bind(this));
      });

      // Setup close triggers
      document.querySelectorAll(selectors.closeBtn).forEach(btn => {
        btn.addEventListener('click', this.close.bind(this));
      });

      // Close on backdrop click
      document.addEventListener('click', this.handleBackdropClick.bind(this));

      // Note toggle functionality
      if (this.noteBtn) {
        this.noteBtn.addEventListener('click', () => {
          this.noteBtn.classList.toggle('is-active');
          this.cartNote.classList.toggle(classes.hide);
        });
      }

      // Cart note save
      if (this.cartNote) {
        this.cartNote.addEventListener('change', () => {
          theme.cart.updateNote(this.cartNote.value);
        });
      }

      // Form submission
      if (this.form) {
        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }

      // Listen for product additions
      document.addEventListener('ajaxProduct:added', this.onProductAdded.bind(this));

      // Listen for cart updates
      document.addEventListener('cart:updated', this.onCartUpdated.bind(this));

      // Close drawer when mobile nav opens
      document.addEventListener('MobileNav:open', this.close.bind(this));

      // Dev-friendly events
      document.addEventListener('cartDrawer:open', this.open.bind(this));
      document.addEventListener('cartDrawer:close', this.close.bind(this));
    },

    open: function(evt) {
      if (evt && evt.preventDefault) {
        evt.preventDefault();
      }

      if (config.isOpen) {
        return;
      }

      // Size drawer height
      theme.sizeDrawer();

      // Add classes to open drawer
      this.drawer.classList.add(classes.drawerOpen);
      document.body.classList.add(classes.bodyOpen);

      // Add overflow hidden to body
      document.body.style.overflow = 'hidden';

      config.isOpen = true;

      // Lock scrolling on mobile
      if (!theme.config.bpSmall) {
        theme.a11y.lockMobileScrolling(config.namespace);
      }

      // Trap focus in drawer
      theme.a11y.trapFocus({
        container: this.drawer,
        namespace: 'cart_drawer_focus'
      });

      // Esc key closes drawer
      window.on('keyup' + config.namespace, evt => {
        if (evt.keyCode === 27) {
          this.close();
        }
      });

      // Dispatch open event
      document.dispatchEvent(new CustomEvent('CartDrawer:open'));
    },

    close: function(evt) {
      if (!config.isOpen) {
        return;
      }

      // Remove classes
      this.drawer.classList.remove(classes.drawerOpen);
      document.body.classList.remove(classes.bodyOpen);

      // Remove overflow hidden from body
      document.body.style.overflow = '';

      config.isOpen = false;

      // Unlock scrolling
      if (!theme.config.bpSmall) {
        theme.a11y.unlockMobileScrolling(config.namespace);
      }

      // Remove focus trap
      theme.a11y.removeTrapFocus({
        container: this.drawer,
        namespace: 'cart_drawer_focus'
      });

      // Remove event listeners
      window.off('keyup' + config.namespace);

      // Dispatch close event
      document.dispatchEvent(new CustomEvent('CartDrawer:close'));
    },

    handleBackdropClick: function(evt) {
      // Only close if clicking on backdrop (body with open class)
      if (evt.target === document.body && config.isOpen) {
        this.close();
      }
    },

    onSubmit: function(evt) {
      this.checkoutBtn.classList.add(classes.btnLoading);

      // Check terms and conditions if enabled
      if (this.termsCheckbox) {
        if (!this.termsCheckbox.checked) {
          evt.preventDefault();
          alert(theme.strings.cartTermsConfirmation || 'Please agree to the terms and conditions');
          this.checkoutBtn.classList.remove(classes.btnLoading);
          return false;
        }
      }
    },

    onProductAdded: function(evt) {
      // Rebuild cart with new product
      if (this.cartForm) {
        this.cartForm.buildCart();
      }

      // Open drawer if not already open
      if (!config.isOpen) {
        this.open();
      }

      document.dispatchEvent(new CustomEvent('cart:updated'));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    },

    onCartUpdated: function(evt) {
      // Cart was updated, could show notification here
      console.log('Cart updated:', evt.detail);
    },

    updateCartContent: function(html) {
      // Parse HTML and update drawer content
      var parser = new DOMParser();
      var doc = parser.parseFromString(html, 'text/html');
      
      var newProducts = doc.querySelector(selectors.products);
      if (newProducts && this.products) {
        this.products.innerHTML = newProducts.innerHTML;
      }

      var newDiscounts = doc.querySelector(selectors.discounts);
      if (newDiscounts && this.discounts) {
        this.discounts.innerHTML = newDiscounts.innerHTML;
      }

      var newSubtotal = doc.querySelector(selectors.subtotal);
      if (newSubtotal && this.subtotal) {
        this.subtotal.innerHTML = newSubtotal.innerHTML;
      }

      // Reinitialize quantity selectors
      if (this.cartForm) {
        this.cartForm.reInit();
      }

      // Trigger gift system update
      document.dispatchEvent(new CustomEvent('cart:updated'));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  });

  return CartDrawer;
})();

// Initialize cart drawer when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Only initialize if not on cart page
  if (!document.body.classList.contains('template-cart')) {
    new theme.CartDrawer();
  }
});